import { useRouter } from "next/router";
import { useCallback, useMemo, useState, useEffect } from "react";
import { Api } from "@vzbl/integrations";
import {
  AnnotatedDeepLink,
  Availability,
  DashboardV1State,
  DeepLinkType,
  DigestV2State,
  ExploreV1State,
  NarrativeV1State,
  toExploreV1State,
} from "pages/api/deep-links/types";
import { ContentViews } from "pages/[orgId]/explore/lib/useContentView";
import { useSpacesQuery, SpaceData } from "pages/[orgId]/space/useSpacesQuery";
import {
  collectionFilters,
  ListPageSort,
  tagFilters,
  typeFilters,
} from "src/components/ListPage";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { compare } from "./AvailabilityStatus";

export type UseLibrary = {
  items: Item[];
  sort: ListPageSort;
  filter: string;
  tags: string[];
  collections: string[];
  setFilter(filter: string): void;
  boardsQuery: ReturnType<typeof useDeepLinks>;
  spacesQuery: ReturnType<typeof useSpacesQuery>;
  queriesQuery: ReturnType<typeof useDeepLinks>;
  topicsQuery: ReturnType<typeof useDeepLinks>;
  narrativesQuery: ReturnType<typeof useDeepLinks>;
};

export function useLibraryWithRouter(options: {
  path: string;
  prefilter?: (item: Item) => boolean;
}): UseLibrary {
  const router = useRouter();
  const { query } = router;
  const filter = `${query.filter || ""}`;
  const sort = `${query.sort || ""}` as ListPageSort;
  const path = options.path;

  // Use local state for the filter
  const [localFilter, setLocalFilter] = useState(filter);

  // Sync local state with URL when URL changes
  useEffect(() => {
    setLocalFilter(filter);
  }, [filter]);

  // Debounce URL updates
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localFilter !== filter) {
        const { query } = router;
        router.replace({
          pathname: `/[orgId]/${path}`,
          query: { ...query, filter: localFilter },
        });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [localFilter, filter, router, path]);

  return useLibrary(
    {
      filter: localFilter,
      sort,
      setFilter: setLocalFilter,
    },
    options
  );
}

export function useLibraryWithoutRouter(options?: {
  prefilter?: (item: Item) => boolean;
}): UseLibrary {
  const [filter, setFilter] = useState("");
  const sort = ListPageSort.alpha_asc;
  return useLibrary(
    { filter, sort, setFilter },
    { path: "", prefilter: options?.prefilter }
  );
}

function useLibrary(
  {
    filter,
    sort,
    setFilter,
  }: { filter: string; sort: ListPageSort; setFilter(filter: string): void },
  options: {
    path: string;
    prefilter?: (item: Item) => boolean;
  }
): UseLibrary {
  const includeBoards = typeFilters.has(filter, ItemType.BOARDS);
  const includeQueries = typeFilters.has(filter, ItemType.QUERIES);
  const includeSpaces = typeFilters.has(filter, ItemType.SPACES);
  const includeTopics = typeFilters.has(filter, ItemType.TOPICS);
  const includeNarratives = typeFilters.has(filter, ItemType.REPORTS);
  const includeAll = !(
    includeBoards ||
    includeQueries ||
    includeSpaces ||
    includeTopics ||
    includeNarratives
  );

  const integrationConfig = useIntegrationConfig().data;

  const spacesQuery = useSpacesQuery("", {
    disabled: !includeSpaces && !includeAll,
  });
  const boardsQuery = useDeepLinks(DeepLinkType.BOARD, {
    disabled: !includeBoards && !includeAll,
  });
  const queriesQuery = useDeepLinks(DeepLinkType.EXPLORE, {
    disabled: !includeQueries && !includeAll,
  });

  const topicsQuery = useDeepLinks(DeepLinkType.DIGEST_2, {
    disabled: !includeTopics && !includeAll,
  });
  const narrativesQuery = useDeepLinks(DeepLinkType.NARRATIVE, {
    disabled: !includeNarratives && !includeAll,
  });

  const boards = useMemo(
    () => boardsQuery.links.data || [],
    [boardsQuery.links.data]
  );
  const queries = useMemo(
    () => queriesQuery.links.data || [],
    [queriesQuery.links.data]
  );
  const topics = useMemo(
    () => topicsQuery.links.data || [],
    [topicsQuery.links.data]
  );
  const narratives = useMemo(
    () => narrativesQuery.links.data || [],
    [narrativesQuery.links.data]
  );
  const spaces = spacesQuery.spaces;

  const items = useMemo(() => {
    const terms = filter.trim().split(" ").filter(Boolean);
    return [
      ...(includeSpaces || includeAll ? spaces : []).map(
        (item) =>
          ({
            ...item,
            itemType: ItemType.SPACES,
            items: [],
          } as SpaceItem)
      ),
      ...(includeBoards || includeAll ? boards : []).map(
        (item) =>
          ({
            ...item,
            itemType: ItemType.BOARDS,
          } as BoardItem)
      ),
      ...(includeQueries || includeAll ? queries : []).map(
        (item) =>
          ({
            ...item,
            json: toExploreV1State(item.state),
            itemType: ItemType.QUERIES,
          } as QueryItem)
      ),
      ...(includeTopics || includeAll ? topics : []).map(
        (item) =>
          ({
            ...item,
            itemType: ItemType.TOPICS,
          } as TopicItem)
      ),
      ...(includeNarratives || includeAll ? narratives : []).map(
        (item) =>
          ({
            ...item,
            itemType: ItemType.REPORTS,
          } as ReportItem)
      ),
    ]
      .filter((item) => {
        if (options.prefilter && !options.prefilter(item)) return false;
        if (terms.length === 0) return true;
        const collection_terms = new Set<string>();
        const scope_terms = new Set<string>();
        const search_terms = new Set<string>();
        const tag_terms = new Set<string>();
        const type_terms = new Set<string>();
        const status_terms = new Set<string>();
        terms.forEach((term) => {
          if (term.startsWith("tag:")) tag_terms.add(term.slice(4));
          else if (term.startsWith("in:")) collection_terms.add(term.slice(3));
          else if (term.startsWith("type:")) type_terms.add(term.slice(5));
          else if (term.startsWith("scope:")) scope_terms.add(term.slice(6));
          else if (term.startsWith("status:")) status_terms.add(term.slice(7));
          else search_terms.add(term.toLowerCase());
        });
        if (
          scope_terms.size > 0 &&
          ![...scope_terms].some((term) => {
            const uid = Boolean((item as any).user_id || (item as any).userId);
            const oid = Boolean((item as any).org_id || (item as any).orgId);
            if (term === "global") return !oid && !uid;
            if (term === "org") return oid && !uid;
            if (term === "private") return oid && uid;
            return true;
          })
        )
          return false;
        if (
          status_terms.size > 0 &&
          item.availability !== undefined &&
          ![...status_terms].some((term) => {
            if (term === "populated") return item.availability?.populated;
            if (term === "notConfigured")
              return item.availability?.configured === false;
            if (term === "processing")
              return (
                item.availability?.populated === false &&
                item.availability?.configured
              );
          })
        )
          return false;
        if (
          tag_terms.size > 0 &&
          ![...tag_terms].every((term) => {
            const tags =
              (item.itemType === ItemType.BOARDS
                ? item.json.tags
                : item.itemType === ItemType.QUERIES
                ? (item.group || "").split(",").filter(Boolean)
                : item.itemType === ItemType.SPACES
                ? item.tags
                : item.itemType === ItemType.TOPICS
                ? item.json.tags
                : item.itemType === ItemType.REPORTS
                ? item.json.tags
                : []) || [];
            return tags.includes(tagFilters.fromFilter(`tag:${term}`));
          })
        )
          return false;
        if (
          type_terms.size > 0 &&
          ![...type_terms].some(
            (term) => item.itemType === typeFilters.fromFilter(`type:${term}`)
          )
        )
          return false;
        if (
          collection_terms.size > 0 &&
          ![...collection_terms].some((term) => {
            if (
              term === "favorites" &&
              (item.itemType === ItemType.BOARDS ||
                item.itemType === ItemType.QUERIES ||
                item.itemType === ItemType.TOPICS ||
                item.itemType === ItemType.REPORTS)
            )
              return item.isBookmarked;
            return false;
          })
        )
          return false;
        return [...search_terms].every(
          (term) =>
            item.name.toLowerCase().includes(term) ||
            (item.description &&
              item.description?.toLowerCase().includes(term)) ||
            item.itemType === ItemType.REPORTS ||
            (item.itemType === ItemType.TOPICS &&
              (item.json.tags || []).some((tag) =>
                tag.toLowerCase().includes(term)
              ))
        );
      })
      .sort((a: any, b: any) =>
        sort === ListPageSort.status_asc
          ? compare(a.availability, b.availability)
          : sort === ListPageSort.status_dsc
          ? -1 * compare(a.availability, b.availability)
          : sort === ListPageSort.scope_asc
          ? a.user_id || a.userId
            ? -1
            : b.user_id || b.userId
            ? 1
            : a.org_id || a.orgId
            ? -1
            : b.org_id | b.orgId
            ? 1
            : 0
          : sort === ListPageSort.scope_dsc
          ? -1 *
            (a.user_id || a.userId
              ? -1
              : b.user_id || b.userId
              ? 1
              : a.org_id || a.orgId
              ? -1
              : b.org_id || b.orgId
              ? 1
              : 0)
          : sort === ListPageSort.type_asc
          ? a.itemType === ItemType.BOARDS
            ? -1
            : b.itemType === ItemType.BOARDS
            ? 1
            : a.itemType === ItemType.QUERIES
            ? -1
            : b.itemType === ItemType.QUERIES
            ? 1
            : a.itemType === ItemType.SPACES
            ? -1
            : 1
          : sort === ListPageSort.type_dsc
          ? a.itemType === ItemType.BOARDS
            ? 1
            : b.itemType === ItemType.BOARDS
            ? -1
            : a.itemType === ItemType.QUERIES
            ? 1
            : b.itemType === ItemType.QUERIES
            ? -1
            : a.itemType === ItemType.SPACES
            ? 1
            : -1
          : sort === ListPageSort.alpha_dsc
          ? b.name.toLowerCase().localeCompare(a.name.toLowerCase())
          : a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
  }, [
    boards,
    boardsQuery,
    filter,
    includeAll,
    includeBoards,
    includeQueries,
    includeSpaces,
    includeTopics,
    includeNarratives,
    options,
    queries,
    sort,
    spaces,
    spacesQuery,
    topics,
    topicsQuery,
    narratives,
    narrativesQuery,
  ]);

  const tags = useMemo(() => {
    const tags = new Map<string, number>();
    items.forEach((item) => {
      if (item.itemType === ItemType.SPACES)
        item.tags.forEach((tag) => tags.set(tag, (tags.get(tag) || 0) + 1));
      if (item.itemType === ItemType.BOARDS)
        item.json.tags?.forEach((tag) =>
          tags.set(tag, (tags.get(tag) || 0) + 1)
        );
      if (item.itemType === ItemType.QUERIES)
        item.group
          ?.split(",")
          .forEach((tag) => tags.set(tag, (tags.get(tag) || 0) + 1));
      if (item.itemType === ItemType.TOPICS)
        item.json.tags?.forEach((tag) =>
          tags.set(tag, (tags.get(tag) || 0) + 1)
        );
      if (item.itemType === ItemType.REPORTS)
        item.json.tags?.forEach((tag) =>
          tags.set(tag, (tags.get(tag) || 0) + 1)
        );
    });
    filter
      .split(" ")
      .filter((term) => term.startsWith("tag:"))
      .forEach((tag) =>
        tags.set(
          tagFilters.fromFilter(tag),
          (tags.get(tagFilters.fromFilter(tag)) || 0) + 1
        )
      );
    return [...tags]
      .sort(([_a, a], [_b, b]) => b - a)
      .slice(0, 18)
      .map((p) => p[0])
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .filter(Boolean);
  }, [items, filter]);

  const collections = useMemo(() => {
    const collections = new Set<string>();
    items.forEach((item) => {
      if (
        (item.itemType === ItemType.BOARDS ||
          item.itemType === ItemType.QUERIES ||
          item.itemType === ItemType.TOPICS ||
          item.itemType === ItemType.REPORTS) &&
        item.isBookmarked
      )
        collections.add("favorites");
    });
    filter
      .split(" ")
      .filter((term) => term.startsWith("in:"))
      .forEach((collection) =>
        collections.add(collectionFilters.fromFilter(collection))
      );
    return [...collections]
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .filter(Boolean);
  }, [items, filter]);

  return {
    items,
    sort,
    filter,
    setFilter,
    tags,
    collections,
    boardsQuery,
    spacesQuery,
    queriesQuery,
    narrativesQuery,
    topicsQuery,
  };
}

export enum ItemType {
  SPACES = "space",
  BOARDS = "board",
  QUERIES = "query",
  TOPICS = "topic",
  REPORTS = "report",
}

export type SpaceItem = { itemType: ItemType.SPACES } & SpaceData & {
    availability?: Availability;
  };
export type BoardItem = {
  itemType: ItemType.BOARDS;
} & AnnotatedDeepLink & {
    json: DashboardV1State;
  } & { availability?: Availability };
export type QueryItem = {
  itemType: ItemType.QUERIES;
} & AnnotatedDeepLink & {
    json: ExploreV1State;
  } & { availability?: Availability };

export type TopicItem = {
  itemType: ItemType.TOPICS;
} & AnnotatedDeepLink & {
    json: DigestV2State;
  } & { availability?: Availability };

export type ReportItem = {
  itemType: ItemType.REPORTS;
} & AnnotatedDeepLink & {
    json: NarrativeV1State;
  } & { availability?: Availability };

export type Item = SpaceItem | BoardItem | QueryItem | TopicItem | ReportItem;
